import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import fromIntensity from "../../../../utils/get-zone";
import { isNotMac } from "../../../../utils/browser";
import convert from "convert-units";
import ToolTip from "../../../../parts/tooltip-portal";
import submit from "submit-form";
import { mapParameter } from "../../../../parts/other-exercise/CustomParametersSets";
import ExerciseLineItem from "./ExerciseLineItem";
import { anySetCompleted } from "../../../../utils/actuals";
import NotesWrapper from "./NotesWrapper";
import ExerciseMetrics from "../../calendar/components/ExerciseMetrics";
import {
  calculatePrefixForExercise,
  buildSupersets,
} from "../../../../utils/prefix-calculator";

import { PARAMETER_OPTIONS } from "../../../../constants";

const { LOAD } = PARAMETER_OPTIONS;

class EditorSessionTableRow extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      currentRow: 0,
      isAddingSet: false,
      newMovement: "",
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
      expanded: false,
    };

    this.localLoadInputs = [];
    this.localIntensityInputs = [];

    this.handleReorder = this.handleReorder.bind(this);
    this._handleOpenAddSet = this._handleOpenAddSet.bind(this);
    this._handleCloseAddSet = this._handleCloseAddSet.bind(this);
    this._handleNewSetChange = this._handleNewSetChange.bind(this);
    this._handleSaveNewSet = this._handleSaveNewSet.bind(this);
    this._handleNewMovementChange = this._handleNewMovementChange.bind(this);
    this._handleSaveNewSetForOther = this._handleSaveNewSetForOther.bind(this);
    this._handleCreateSuperset = this._handleCreateSuperset.bind(this);
    this._handleRemoveSuperset = this._handleRemoveSuperset.bind(this);
    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
  }

  componentDidMount() {
    if (window.innerWidth <= 960) {
      this.setState({ expanded: true });
    }
    window.addEventListener("resize", this.updateDimensions);
  }

  updateDimensions() {
    if (window.innerWidth <= 960) {
      this.setState({ expanded: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateDimensions);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.exercise !== this.props.exercise) {
      this.setState({ newMovement: "" });
    }
  }

  hasNumber(string) {
    return /\d/.test(string);
  }

  handleReorder({ exercise, exercises, to, isProgramEditor, athlete, e }) {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    // handle optimistic updates
    const exerciseChangedIndex = this.props.exercises.findIndex(
      (ex) => ex._id === exercise
    );
    let canReorder = true;

    // if move up, look for the next superset and move on top of it
    if (to < 0) {
      if (exerciseChangedIndex > 1) {
        const previousExercise = this.props.exercises[exerciseChangedIndex - 1];
        const nextToPreviousExercise =
          this.props.exercises[exerciseChangedIndex - 2];
        if (nextToPreviousExercise.nextSuperset === previousExercise._id) {
          canReorder = false;
        }
      }
    } else {
      const nextExercise = this.props.exercises[exerciseChangedIndex + 1];
      if (nextExercise.nextSuperset) {
        canReorder = false;
      }
    }

    if (!canReorder) {
      alert(
        "Destination cannot be a superset. Please remove the superset on the exercise and try again"
      );
      return;
    }

    if (this.props.exercises[exerciseChangedIndex].nextSuperset) {
      canReorder = false;
    }
    if (exerciseChangedIndex > 0) {
      const previousExercise = this.props.exercises[exerciseChangedIndex - 1];
      if (previousExercise && previousExercise.nextSuperset) {
        canReorder = false;
      }
    }

    if (!canReorder) {
      alert("Cannot reorder an exercise within a superset");
      return;
    }

    if (isProgramEditor) {
      submit(`/actions/exercise/order?proxyAthlete=${athlete}`, {
        method: "POST",
        body: {
          cycle: this.props.cycle._id,
          program: this.props.programOrder,
          session: this.props.session.order,
          week: this.props.session.week,
          exercises: this.props.exercises,
          exercise,
          to,
        },
      });
    } else {
      submit(`/actions/exercise/single-workout-order?proxyAthlete=${athlete}`, {
        method: "POST",
        body: {
          exercises: this.props.exercises,
          exercise,
          to,
        },
      });
    }
  }

  // Add Set functions
  // handles the opening of the Add set line
  _handleOpenAddSet(e, setIndex) {
    e.preventDefault();
    this.setState({ isAddingSet: true, currentRow: setIndex });
  }
  // handles the closing of the Add set line
  // if the add set line is closed, we should reset all the states
  _handleCloseAddSet(e) {
    e.preventDefault();
    this.setState({
      isAddingSet: false,
      currentRow: 0,
      sets: 0,
      reps: 0,
      rpe: "",
      load: 0,
      percentRepMax: 0,
    });
  }
  // handles any changes for the fields in the add set line
  _handleNewSetChange({ target: { name, value } }, defaultRepMax) {
    const activeExercise = _.find(this.props.exercises, {
      _id: this.props.exercise,
    });
    const isAssistant = activeExercise.type === "assistant";

    // If the field is load or pRM, calculate and set states.
    if (name === "load" || name === "percentRepMax") {
      // if name is load
      if (name === "load") {
        this.setState({
          percentRepMax: isAssistant
            ? 0
            : _.round((value / defaultRepMax) * 100, 1),
          load: value,
        });
      }
      // else if name is percentRepMax
      else if (name === "percentRepMax" && !isAssistant) {
        this.setState({
          load: _.round((defaultRepMax * value) / 100, 2),
          percentRepMax: value,
        });
      }
      if (!isAssistant) {
        // recalculating the fields to be shown.
        const localLoadInput = this.localLoadInputs["new"];
        if (localLoadInput) {
          if (name === "percentRepMax") {
            localLoadInput.value = _.round((defaultRepMax * value) / 100, 2);
          } else {
            this.localIntensityInputs["new"].value = _.round(
              (value / defaultRepMax) * 100,
              1
            );
          }
        }
      }
    }
    // else set state dynamically
    else {
      this.setState({ [name]: value });
    }
  }
  // submits the newly entered set into the exercise
  _handleSaveNewSet(e, isEquipped, defaultRepMax, activeUser, exId, setIndex) {
    e.preventDefault();
    const { sets, reps, rpe, load, percentRepMax } = this.state;
    const { _id } = activeUser;
    const { units: coachUnits } = this.context.locals.activeUser;
    const isRepRange = reps && reps.includes("-");
    const repsRange = isRepRange ? reps.split("-") : reps;

    submit(`/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${_id}`, {
      method: "POST",
      body: {
        newSet: {
          sets,
          reps: isRepRange ? repsRange[1] || repsRange[0] : reps,
          percentRepMax,
          load:
            coachUnits === "imperial"
              ? _.round(convert(load).from("lb").to("kg"), 2)
              : load,
          equipped: isEquipped,
          zone: fromIntensity(percentRepMax, reps),
          rpe,
          repsLower: isRepRange && repsRange[1] ? repsRange[0] : null,
        },
        defaultRepMax,
      },
    });

    this._handleCloseAddSet(e);
  }

  // submits the newly entered set into the exercise
  _handleSaveNewSetForOther(e, exId, setIndex, activeUser, activeExercise) {
    e.preventDefault();

    let newSet = {};
    const { units: coachUnits } = this.context.locals.activeUser;

    activeExercise.parameters.forEach((elem) => {
      if (elem === LOAD && coachUnits === "imperial") {
        newSet[mapParameter(elem)] = _.round(
          convert(this.state[mapParameter(elem)]).from("lb").to("kg"),
          2
        );
      } else {
        newSet[mapParameter(elem)] = this.state[mapParameter(elem)];
      }
    });
    submit(
      `/actions/exercise/quick-edit/sets/add/${setIndex}/${exId}/${activeUser._id}`,
      {
        method: "POST",
        body: {
          newSet,
          defaultRepMax: "",
        },
      }
    );

    this._handleCloseAddSet(e);
  }

  _handleNewMovementChange(value, exId, setIndex) {
    const { handleQuickEditChange } = this.props;

    if (value) {
      this.setState({ newMovement: `${value.movementLabel}` });
      const e = {
        target: { name: "exerciseType", value },
      };

      handleQuickEditChange(e, exId, setIndex);
    }
  }

  _handleCreateSuperset(fullExercise, exercise, nextExercise) {
    submit(`/actions/exercise/superset/add`, {
      method: "POST",
      body: {
        exercise,
        nextExercise,
        fullExercise,
      },
    });
  }

  _handleRemoveSuperset(fullExercise, exercise) {
    submit(`/actions/exercise/superset/remove`, {
      method: "POST",
      body: {
        exercise,
        fullExercise,
      },
    });
  }

  // handles expanding the outcomes in the actual results
  toggleExpanded() {
    this.setState({ expanded: !this.state.expanded });
  }

  render() {
    const { query, withWeight } = this.context.locals;
    const {
      exercises,
      exercise,
      exerciseOrder,
      fromMetric,
      activeUser,
      handleDuplicate,
      isProgramEditor,
      openQuickEditMode,
      handleQuickEditChange,
      loadInputs,
      intensityInputs,
      isQuickEditOpen,
      LineItemComponent = ExerciseLineItem,
    } = this.props;
    const {
      isAddingSet,
      currentRow,
      load,
      sets,
      rpe,
      reps,
      percentRepMax,
      newMovement,
      expanded,
    } = this.state;
    const newSet = {
      sets,
      load,
      rpe,
      reps,
      percentRepMax,
    };
    const activeUserToRequest = this.context.locals.activeUserToRequest;
    const activeExercise = _.find(exercises, { _id: exercise });
    const supersets = buildSupersets(exercises);
    const activeExerciseIndex = _.findIndex(
      exercises,
      (ex) => ex._id == activeExercise._id
    );

    if (activeExercise && activeExercise.sets) {
      const isFirst = exerciseOrder === 0;
      const isLast = exerciseOrder === exercises.length - 1;
      const nextExercise = !isLast && exercises[activeExerciseIndex + 1];
      const isSuperset = activeExercise.nextSuperset === nextExercise._id;
      const prevHandleReorder = (e) =>
        this.handleReorder({
          exercise: activeExercise._id,
          exercises,
          to: -1,
          isProgramEditor,
          athlete: query.athlete,
          e,
        });
      const nextHandleReorder = (e) =>
        this.handleReorder({
          exercise: activeExercise._id,
          exercises,
          to: 1,
          isProgramEditor,
          athlete: query.athlete,
          e,
        });

      let prefix = "";
      if (activeExerciseIndex >= 0) {
        prefix = calculatePrefixForExercise(activeExercise._id, supersets);
      }
      let prefixHasNumber = this.hasNumber(prefix);
      const isAnySetCompleted = anySetCompleted(activeExercise);
      const isAnySetCompletedNext =
        nextExercise && anySetCompleted(nextExercise);
      const intensityMeasure =
        _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
          ? "RPE"
          : "RiR";
      const isRiR = intensityMeasure === "RiR";
      const editable = !activeExercise.sets.some((set) => set.outcomes);
      return (
        <>
          <li
            className={`Program-Editor-Exercise ${this.props.altClass || ""} ${
              isNotMac() ? "padding-scroll-no-mac" : ""
            } ${isSuperset ? "is-superset" : ""} ${
              prefixHasNumber && prefix.slice(1) != "1"
                ? "not-first-superset"
                : ""
            } ${
              prefixHasNumber && activeExercise.nextSuperset
                ? "not-last-superset"
                : ""
            } ${isLast ? "last-exercise-list" : ""} ${
              isFirst ? "first-exercise-list" : ""
            } ${isAnySetCompleted && !isProgramEditor ? "is-actuals" : ""}`}
            data-id={activeExercise._id}
            id={activeExercise._id}
            onClick={(e) => {
              if (editable) {
                openQuickEditMode(e);
              }
            }}
          >
            {_.map(activeExercise.sets, (set, setIndex) => {
              return (
                <LineItemComponent
                  key={`${activeExercise._id}${setIndex}`}
                  handleQuickEditChange={handleQuickEditChange}
                  _handleOpenAddSet={this._handleOpenAddSet}
                  _handleCloseAddSet={this._handleCloseAddSet}
                  _handleNewSetChange={this._handleNewSetChange}
                  _handleSaveNewSet={this._handleSaveNewSet}
                  _handleNewMovementChange={this._handleNewMovementChange}
                  set={set}
                  newSet={newSet}
                  setIndex={setIndex}
                  activeExercise={activeExercise}
                  stateExercise={this.props.stateExercise}
                  isProgramEditor={isProgramEditor}
                  fromMetric={fromMetric}
                  activeUser={activeUser}
                  withWeight={withWeight}
                  loadInputs={loadInputs}
                  intensityInputs={intensityInputs}
                  localLoadInputs={this.localLoadInputs}
                  localIntensityInputs={this.localIntensityInputs}
                  isQuickEditOpen={isQuickEditOpen}
                  isAddingSet={isAddingSet}
                  currentRow={currentRow}
                  units={this.context.locals.activeUser.units}
                  newMovement={newMovement}
                  setLength={activeExercise.sets.length}
                  activeContextUser={activeUserToRequest}
                  activeExerciseNotes={activeExercise.notes}
                  exercises={exercises}
                  _handleSaveNewSetForOther={this._handleSaveNewSetForOther}
                  prevHandleReorder={prevHandleReorder}
                  nextHandleReorder={nextHandleReorder}
                  isFirst={isFirst}
                  isLast={isLast}
                  isAnySetCompleted={isAnySetCompleted}
                  prefix={prefix}
                  expanded={expanded}
                  toggleExpanded={this.toggleExpanded}
                  intensityMeasure={intensityMeasure}
                  isRiR={isRiR}
                  editable={editable}
                  handleDuplicate={handleDuplicate}
                />
              );
            })}

            <NotesWrapper
              isProgramEditor={isProgramEditor}
              openQuickEditMode={isQuickEditOpen}
              onInputHandleQuickEditChange={(e) =>
                handleQuickEditChange(e, activeExercise._id, null)
              }
              activeExercise={activeExercise}
            />

            {isQuickEditOpen &&
              nextExercise &&
              !isLast &&
              !isSuperset &&
              !isAnySetCompleted &&
              !isAnySetCompletedNext && (
                <button
                  data-button-type="text"
                  className="superset-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    this._handleCreateSuperset(
                      activeExercise,
                      activeExercise._id,
                      nextExercise._id
                    );
                  }}
                >
                  <img
                    src="/img/new-icons/add-gradient-icon.svg"
                    height={16}
                    width={16}
                  />
                  <span>Superset</span>
                </button>
              )}

            {isQuickEditOpen && nextExercise && !isLast && isSuperset && (
              <button
                data-button-type="text"
                className="superset-button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this._handleRemoveSuperset(
                    activeExercise,
                    activeExercise._id,
                    nextExercise._id
                  );
                }}
              >
                <img
                  src="/img/new-icons/red-trash.svg"
                  height={16}
                  width={16}
                />
                <span
                  style={{
                    background: "none",
                    color: "var(--danger)",
                  }}
                >
                  Remove Superset
                </span>
              </button>
            )}
          </li>
          <ExerciseMetrics
            exercise={activeExercise}
            fromMetric={fromMetric}
            withWeight={withWeight}
            units={this.context.locals.activeUser.units}
            isProgramEditor={isProgramEditor}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
          />
        </>
      );
    }
    return null;
  }
}

EditorSessionTableRow.contextTypes = { locals: PropTypes.object };

export default EditorSessionTableRow;
